import {
  initializeVoiceToText,
  requestMicrophonePermission,
} from "./voiceBridge";

let isVoiceCapabilityReady = false;
let voiceCapabilityPromise: Promise<boolean> | null = null;
let voiceCapabilityPromiseVersion: number | null = null;
let voiceWarmupPromise: Promise<boolean> | null = null;
let voiceWarmupPromiseVersion: number | null = null;
let capabilityVersion = 0;
let lifecycleSubscriberCount = 0;

export function invalidateVoiceCapability() {
  capabilityVersion += 1;
  isVoiceCapabilityReady = false;
}

const handleVisibilityChange = () => {
  if (document.visibilityState === "visible") {
    invalidateVoiceCapability();
  }
};

const handlePageShow = () => invalidateVoiceCapability();

/**
 * 共享当前页面内的语音权限和 ASR 初始化结果。
 * 页面重新可见后会在下一次预热或用户点击语音输入时重新检查权限。
 */
export function subscribeVoiceCapabilityLifecycle() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return () => undefined;
  }

  lifecycleSubscriberCount += 1;
  if (lifecycleSubscriberCount === 1) {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);
  }

  return () => {
    lifecycleSubscriberCount = Math.max(0, lifecycleSubscriberCount - 1);
    if (lifecycleSubscriberCount === 0) {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
      invalidateVoiceCapability();
    }
  };
}

function cacheVoiceCapabilityIfCurrent(
  isReady: boolean,
  version: number
) {
  if (isReady && version === capabilityVersion) {
    isVoiceCapabilityReady = true;
  }
  return isReady;
}

/**
 * 静默预热 ASR：只执行初始化和权限状态检查，不主动请求麦克风权限。
 * 同页多个语音输入共享预热任务，失败后仍可在用户点击时正常重试。
 */
export function prepareVoiceCapability() {
  if (isVoiceCapabilityReady) return Promise.resolve(true);
  if (
    voiceCapabilityPromise &&
    voiceCapabilityPromiseVersion === capabilityVersion
  ) {
    return voiceCapabilityPromise;
  }
  if (
    voiceWarmupPromise &&
    voiceWarmupPromiseVersion === capabilityVersion
  ) {
    return voiceWarmupPromise;
  }

  const currentVersion = capabilityVersion;
  const warmup = initializeVoiceToText().then((isReady) =>
    cacheVoiceCapabilityIfCurrent(isReady, currentVersion)
  );

  voiceWarmupPromise = warmup;
  voiceWarmupPromiseVersion = currentVersion;
  return warmup.finally(() => {
    if (voiceWarmupPromise === warmup) {
      voiceWarmupPromise = null;
      voiceWarmupPromiseVersion = null;
    }
  });
}

/**
 * 首次调用会检查/请求麦克风权限并初始化 ASR；同页并发调用共享同一请求。
 * 已有权限时首次初始化结果直接复用；仅在刚完成授权后再次初始化 ASR。
 */
export function ensureVoiceCapability() {
  if (isVoiceCapabilityReady) return Promise.resolve(true);
  if (
    voiceCapabilityPromise &&
    voiceCapabilityPromiseVersion === capabilityVersion
  ) {
    return voiceCapabilityPromise;
  }

  const currentVersion = capabilityVersion;
  const initialization = (async () => {
    const currentWarmup =
      voiceWarmupPromiseVersion === currentVersion
        ? voiceWarmupPromise
        : null;
    const isAlreadyReady = currentWarmup
      ? await currentWarmup
      : await initializeVoiceToText();

    if (isAlreadyReady) {
      return cacheVoiceCapabilityIfCurrent(true, currentVersion);
    }

    const isPermissionGranted = await requestMicrophonePermission();
    if (!isPermissionGranted) return false;

    const isInitialized = await initializeVoiceToText();
    return cacheVoiceCapabilityIfCurrent(isInitialized, currentVersion);
  })();

  voiceCapabilityPromise = initialization;
  voiceCapabilityPromiseVersion = currentVersion;
  return initialization.finally(() => {
    if (voiceCapabilityPromise === initialization) {
      voiceCapabilityPromise = null;
      voiceCapabilityPromiseVersion = null;
    }
  });
}
